import type { IAuditLog } from '../../src/engine/AuditLog';
import type { IOperationLogEntry } from '../../src/models/Operation';
import type { IGraphClient } from '../../src/services/GraphService';
import {
  FIELDS, fromListFields, type IAuditListItem, type IFieldDefinition, LIST_TITLE, LIST_URL_NAME, toListFields
} from '../../src/services/auditLogSchema';

interface IGraphListItem {
  fields: IAuditListItem;
}

interface IGraphPage<T> {
  value: T[];
  '@odata.nextLink'?: string;
}

function statusOf(error: unknown): number | undefined {
  return (error as { statusCode?: number } | undefined)?.statusCode;
}

function columnDefinition(field: IFieldDefinition): object {
  const base: { name: string; displayName: string; indexed?: boolean } = { name: field.name, displayName: field.name };
  if (field.indexed) {
    base.indexed = true;
  }
  switch (field.type) {
    case 'Note': return { ...base, text: { allowMultipleLines: true, linesForEditing: 3 } };
    case 'Number': return { ...base, number: {} };
    case 'Boolean': return { ...base, boolean: {} };
    default: return { ...base, text: {} };
  }
}

/**
 * The audit list through Microsoft Graph, for the web app. Same list, columns and item shape
 * as the SPFx web part writes (see auditLogSchema), so history carries over between them.
 *
 * Reading needs only Sites.Read.All. Creating and writing need Sites.Manage.All, which is
 * requested the first time someone applies or undoes changes.
 */
export class GraphAuditLog implements IAuditLog {
  public readonly viewUrl: string;
  private _listId: string | undefined;
  private _ready: boolean = false;

  constructor(
    private readonly _read: IGraphClient,
    private readonly _manage: IGraphClient,
    private readonly _siteId: string,
    siteWebUrl: string
  ) {
    this.viewUrl = `${siteWebUrl.replace(/\/$/, '')}/Lists/${LIST_URL_NAME}/AllItems.aspx`;
  }

  public async ensure(): Promise<void> {
    if (this._ready) {
      return;
    }
    let listId: string | undefined = await this._findList(this._manage);
    if (!listId) {
      const created: { id: string } = await this._manage.api(`/sites/${this._siteId}/lists`).post({
        displayName: LIST_URL_NAME, // Sets the URL name; the title is changed below.
        list: { template: 'genericList', hidden: true },
        columns: FIELDS.map(columnDefinition)
      });
      listId = created.id;
      // Cosmetic; the list works under either title.
      await this._manage.api(`/sites/${this._siteId}/lists/${listId}`).patch({ displayName: LIST_TITLE }).catch(() => undefined);
    } else {
      await this._ensureColumns(listId);
    }
    this._listId = listId;
    this._ready = true;
  }

  public async write(entry: IOperationLogEntry): Promise<void> {
    await this._manage.api(`/sites/${this._siteId}/lists/${this._listId}/items`).post({ fields: toListFields(entry) });
  }

  public async readEntries(libraryId: string): Promise<IOperationLogEntry[]> {
    const listId: string | undefined = this._listId ?? await this._findList(this._read);
    if (!listId) {
      return []; // Nothing has been applied on this site yet.
    }
    const existing: IGraphPage<{ name: string }> = await this._read.api(`/sites/${this._siteId}/lists/${listId}/columns?$select=name`).get();
    const names: { [name: string]: boolean } = {};
    for (const c of existing.value) {
      names[c.name] = true;
    }
    // Lists created by earlier versions may lack newer columns until the next apply adds them.
    const select: string = FIELDS.map(f => f.name).filter(n => names[n]).join(',');

    const entries: IOperationLogEntry[] = [];
    let url: string | undefined = `/sites/${this._siteId}/lists/${listId}/items?$expand=fields($select=${select})&$top=500`;
    while (url) {
      const page: IGraphPage<IGraphListItem> = await this._read.api(url).get();
      for (const item of page.value) {
        if (item.fields.LibraryId === libraryId) {
          entries.push(fromListFields(item.fields));
        }
      }
      url = page['@odata.nextLink'];
    }
    // Graph returns items in creation order; keep the log's own order stable regardless.
    return entries.sort((a, b) => a.timestamp.localeCompare(b.timestamp) || a.operationId.localeCompare(b.operationId));
  }

  private async _findList(client: IGraphClient): Promise<string | undefined> {
    // Graph accepts a list's title in place of its ID. Older lists may still carry the URL name.
    for (const title of [LIST_TITLE, LIST_URL_NAME]) {
      try {
        const list: { id: string } = await client.api(`/sites/${this._siteId}/lists/${encodeURIComponent(title)}?$select=id`).get();
        return list.id;
      } catch (error) {
        if (statusOf(error) !== 404) {
          throw error;
        }
      }
    }
    return undefined;
  }

  private async _ensureColumns(listId: string): Promise<void> {
    const existing: IGraphPage<{ name: string }> = await this._manage.api(`/sites/${this._siteId}/lists/${listId}/columns?$select=name`).get();
    const names: { [name: string]: boolean } = {};
    for (const c of existing.value) {
      names[c.name] = true;
    }
    for (const field of FIELDS) {
      if (!names[field.name]) {
        await this._manage.api(`/sites/${this._siteId}/lists/${listId}/columns`).post(columnDefinition(field));
      }
    }
  }
}
